import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellSlash, SpeakerHigh } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { playNotifChime, speakAnnouncement, unlockAudio } from "@/lib/notifChime";
import { formatRp } from "@/lib/constants";
import { useShopNotif } from "@/context/ShopNotifContext";

const SEEN_KEY = "gbs_notif_seen";
const ON_KEY = "gbs_notif_on";
const VOL_KEY = "gbs_notif_vol";
const TONE_KEY = "gbs_notif_tone";
const CHIME_KEY = "gbs_notif_chime";
const VOICE_KEY = "gbs_notif_voice";
const VOICE_MODE_KEY = "gbs_notif_voice_mode";

export default function NotificationBell({ hcItems = [], hcTotal = 0 }) {
  const navigate = useNavigate();
  const shop = useShopNotif();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(() => (localStorage.getItem(ON_KEY) ?? "on") === "on");
  const [chimeOn, setChimeOn] = useState(() => (localStorage.getItem(CHIME_KEY) ?? "on") === "on");
  const [pending, setPending] = useState(0);
  const [seen, setSeen] = useState(() => localStorage.getItem(SEEN_KEY) || "");
  const knownIds = useRef(null);

  const poll = useCallback(() => {
    api.get("/inbox-counts").then((r) => setPending(r.data?.booking_masuk || 0)).catch(() => {});
    api.get("/bookings/notifications").then((r) => {
      const data = r.data || [];
      setItems(data);
      const ids = new Set(data.map((x) => x.id));
      if (knownIds.current === null) { knownIds.current = ids; return; }
      const fresh = data.filter((x) => !knownIds.current.has(x.id));
      knownIds.current = ids;
      if (fresh.length && (localStorage.getItem(ON_KEY) ?? "on") === "on") {
        const vol = parseFloat(localStorage.getItem(VOL_KEY) ?? "0.9");
        const chimeOn = (localStorage.getItem(CHIME_KEY) ?? "on") === "on";
        if (chimeOn) playNotifChime(vol, localStorage.getItem(TONE_KEY) || "bandara");
        const f = fresh[0];
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("Reservasi Baru — GBS Clinic", {
            body: `${f.nama} · ${f.jenis_layanan || ""}\n${f.tanggal_kunjungan || ""} ${f.jam_kunjungan || ""} · ${f.lokasi} · ${f.payment_status || ""}`,
          });
        }
        if ((localStorage.getItem(VOICE_KEY) ?? "off") === "on") {
          const mode = localStorage.getItem(VOICE_MODE_KEY) || "name";
          const first = String(f.nama || "").trim().split(/\s+/)[0] || "";
          let phrase;
          if (fresh.length > 1) phrase = `Ada ${fresh.length} booking baru masuk.`;
          else if (mode === "generic" || !first) phrase = "Ada booking baru masuk.";
          else if (mode === "name_service") phrase = `Ada booking baru dari ${first} untuk ${f.jenis_layanan || "layanan"}.`;
          else phrase = `Ada booking baru dari ${first}.`;
          setTimeout(() => speakAnnouncement(phrase, vol), chimeOn ? 2700 : 250);
        }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (enabled && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [enabled, poll]);

  // Real-time SSE (near-instant). Polling above stays as the fallback; the SSE
  // event just triggers an immediate poll (dedup via knownIds -> no double chime).
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    let es;
    try {
      es = new EventSource(`${API}/bookings/notifications/stream?token=${encodeURIComponent(token)}`);
      es.addEventListener("notif", () => poll());
      es.onerror = () => { try { es.close(); } catch (_) {} };
    } catch (_) { /* fallback to polling */ }
    return () => { try { es && es.close(); } catch (_) {} };
  }, [poll]);

  const testSound = () => {
    unlockAudio();
    const vol = parseFloat(localStorage.getItem(VOL_KEY) ?? "0.9");
    playNotifChime(vol, localStorage.getItem(TONE_KEY) || "bandara");
  };

  const toggleChime = () => {
    const next = !chimeOn;
    setChimeOn(next);
    localStorage.setItem(CHIME_KEY, next ? "on" : "off");
    if (next) testSound();
  };

  const unread = pending + (shop.unread || 0) + (hcTotal || 0);

  const ROLE_LABEL = { client: "Klien", staff: "Petugas", admin: "Admin" };
  const witaTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" }) + " WITA"; }
    catch (e) { return ""; }
  };
  // Room Chat notif: opening the room (not the dropdown) is what clears unread. Invalid id
  // is handled by the target page (toast + back to list). No mark-read here.
  const openRoomItem = (it) => {
    setOpen(false);
    navigate(`/homecare-chat-admin?room=${it.room_id}`);
  };

  const openItem = (it) => {
    const now = new Date().toISOString();
    localStorage.setItem(SEEN_KEY, now); setSeen(now);
    setOpen(false);
    navigate(it.type === "order" ? "/pesanan" : it.type === "payment" ? "/pembayaran" : "/booking-masuk");
  };

  const markAll = () => {
    const now = new Date().toISOString();
    localStorage.setItem(SEEN_KEY, now); setSeen(now);
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(ON_KEY, next ? "on" : "off");
    if (next && "Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
  };

  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={() => { setOpen((o) => !o); if (!open) markAll(); }} className="relative rounded-xl p-2 text-slate-600 transition-colors hover:bg-pink-50">
        {enabled ? <Bell size={24} weight="duotone" /> : <BellSlash size={24} weight="duotone" />}
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#EC4899] px-1 text-[10px] font-bold text-white">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel" className="absolute right-0 z-50 mt-2 w-80 max-w-[90vw] rounded-2xl border border-pink-100 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="font-heading text-sm font-bold text-slate-800">Notifikasi</span>
              <button data-testid="notif-toggle" onClick={toggle} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                Notifikasi: {enabled ? "ON" : "OFF"}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
              <button data-testid="notif-sound-toggle" onClick={toggleChime} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${chimeOn ? "bg-pink-100 text-[#DB2777]" : "bg-slate-100 text-slate-500"}`}>
                <SpeakerHigh size={13} weight="fill" /> Suara: {chimeOn ? "ON" : "OFF"}
              </button>
              <button data-testid="notif-test-sound" onClick={testSound} className="rounded-full border border-pink-200 px-2.5 py-0.5 text-[11px] font-bold text-[#DB2777] transition-colors hover:bg-pink-50">
                Tes Suara
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {hcItems && hcItems.length > 0 && hcItems.map((it) => (
                <button key={`hc-${it.room_id}`} data-testid={`notif-roomchat-${it.room_id}`} onClick={() => openRoomItem(it)} className="flex w-full items-center justify-between gap-2 border-b border-purple-100 bg-[#F7F0FF] px-4 py-2.5 text-left transition-colors hover:bg-[#EEE6FF]">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[10px] font-bold uppercase tracking-wide text-[#6A38C7]">Pesan Room Chat</span>
                    <span className="block truncate text-sm font-bold text-slate-800">{it.client_name}</span>
                    <span className="block text-xs text-slate-500">{ROLE_LABEL[it.last_sender_role] || "Pesan"}{it.last_at ? ` · ${witaTime(it.last_at)}` : ""}</span>
                  </span>
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-extrabold text-white">{it.unread > 99 ? "99+" : it.unread}</span>
                </button>
              ))}
              {shop.unread > 0 && (
                <button data-testid="notif-shop-row" onClick={() => { setOpen(false); navigate("/pesanan-toko"); }} className="flex w-full items-center justify-between gap-2 border-b border-pink-100 bg-[#FFF5FA] px-4 py-2.5 text-left transition-colors hover:bg-pink-50">
                  <span className="text-sm font-bold text-[#DB2777]">Pesanan Toko perlu tindakan</span>
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-extrabold text-white">{shop.unread > 99 ? "99+" : shop.unread}</span>
                </button>
              )}
              {items.length === 0 ? (
                (!hcItems || hcItems.length === 0) && shop.unread === 0 ? <p className="px-4 py-6 text-center text-sm text-slate-400">Belum ada notifikasi.</p> : null
              ) : items.slice(0, 15).map((it) => (
                <button key={it.id} data-testid={`notif-item-${it.id}`} onClick={() => openItem(it)} className="flex w-full flex-col items-start gap-0.5 border-b border-slate-50 px-4 py-2.5 text-left transition-colors hover:bg-pink-50">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#DB2777]">Booking baru</span>
                  <span className="text-sm font-bold text-slate-800">{it.nama}</span>
                  <span className="text-xs text-slate-500">{it.jenis_layanan}</span>
                  <span className="text-xs text-slate-400">{it.tanggal_kunjungan} {it.jam_kunjungan} · {it.lokasi} · {it.payment_status}{it.type === "order" && it.grand_total ? ` · Est ${formatRp(it.grand_total)}` : ""}</span>
                </button>
              ))}
            </div>
            <button onClick={() => { markAll(); setOpen(false); navigate("/booking-masuk"); }} className="w-full rounded-b-2xl bg-[#FCE7F3] py-2.5 text-center text-sm font-bold text-[#DB2777] hover:bg-pink-100">Lihat Semua Booking Masuk</button>
          </div>
        </>
      )}
    </div>
  );
}
